import {Injectable} from '@angular/core';

import {Location} from '@angular/common';
import {HttpClient, HttpStatusCode} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {Router} from '@angular/router';
import {Store} from '@ngxs/store';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {AccessType, ConnectionStatus, Role} from '@app/modules/shared/data/constants';
import {CoreService} from '@app/services/core.service';
import {WebPortalMessagingService} from '@app/services/web-portal-messaging.service';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {VcrEntity} from '@app/models/vcr-entity/entity';
import {AppBaseInfo} from '@app/models/vcr-entity/app-base-info';
import {User} from '@app/models/vcr-entity/user';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {CrossInjectionService} from '@app/modules/shared/services/cross-injection.service';
import {MessageHandleService} from '@app/modules/shared/services/message-handle.service';
import {RemoteUserAdditionalInfoStateModel, RemoteUsersInfoState} from '@app/state/remote-users.state';
import {RemoteParticipantInfo} from '@app/models/remote-participant-info';
import {ClassroomInitService} from '@app/modules/shared/services/classroom-init.service';
import {Observable} from 'rxjs';
import {ServiceInfoAction} from '@app/state/service-info.state';
import {DiagnosticsService} from '@app/modules/diagnostics/services/diagnostics.service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '@app/modules/shared/services/navigation.service';

@Injectable()
export class AppService {
  private classroom: ClassroomStateModel;
  readonly contextPath = '/vcr_api';
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};
  private localUser: LocalUser;

  constructor(
    private coreService: CoreService,
    private messageHandleService: MessageHandleService,
    private crossInjectionService: CrossInjectionService,
    private classroomInitService: ClassroomInitService,
    private diagnosticsService: DiagnosticsService,
    private store: Store,
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private navigationService: NavigationService,
    private webPortalCommandListenerService: WebPortalMessagingService,
    private location: Location,
    private title: Title
  ) {
    this.listenStoreChanges();
  }

  public initWebPortalCommandListeners(): void {
    this.webPortalCommandListenerService.init();
  }

  public onAppBeforeInitialization(): Promise<any> {
    this.initGroup();
    return this.http.get(`${this.classroom.vcrURL}/app/pre-init-info`).pipe(map((result: any) => {

      const appBaseInfo: AppBaseInfo = VcrEntity.createEntity(AppBaseInfo, result.data);

      if (appBaseInfo.vcrTitle) {
        this.title.setTitle(appBaseInfo.vcrTitle);
      }

      // Set to vcrVersion
      this.store.dispatch(new ServiceInfoAction.SetVersion(appBaseInfo.version));
      this.store.dispatch(new ServiceInfoAction.SetHostname(appBaseInfo.hostname));
      this.coreService.setCurrentClass(appBaseInfo.currentClass);
      this.store.dispatch(new ClassroomAction.SetServiceUrls(appBaseInfo.cmsUrl, appBaseInfo.studentPortalUrl, appBaseInfo.newStudentPortalUrl));

      if (appBaseInfo.sessionUser) {
        const localUser: User = VcrEntity.createEntity(User, appBaseInfo.sessionUser);
        this.store.dispatch(new LocalUserAction.SetLocalUser(new LocalUser(localUser)));
        this.store.dispatch(new LocalUserAction.SetIsCurrentClassParticipant(appBaseInfo.isCurrentClassParticipant));
      }

      this.store.dispatch(new ClassroomAction.ToggleDnDMode(appBaseInfo.isInDNDMode));

      // Set IPad mode
      let iPadMode: boolean = navigator.userAgent.match(/iPad|iPhone/i) != null;
      if (!iPadMode) {
        iPadMode = (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
      }
      this.store.dispatch(new ClassroomAction.SetIPadMode(iPadMode));
      // Check required data
      if (appBaseInfo.roomNum === null || (
          appBaseInfo.roomNum === 0 &&
          !this.location.path().includes('/diagnostics') &&
          !this.location.path().includes('/report'))
      ) {
        // redirect to incorrect url
        this.router.navigateByUrl(`/incorrect-url`).then();
        return;
      } else {
        if (
            (
              this.location.path().includes('/diagnostics') &&
              !this.location.path().includes('/report') &&
              !this.location.path().includes('/login')
            ) ||
            this.router.url.includes('/guest')
        ) {

          if (!appBaseInfo.sessionUser) {
            if (this.classroom.roomNum === 0) {
              this.navigationService.navigateWithoutRouting(`/diagnostics/enterPage`);
            } else {
              this.router.navigateByUrl(`/${this.classroom.roomNum}/diagnostics/enterPage`).then();
            }
            return;
          } else {
            if (this.classroom.roomNum === 0) {
              this.navigationService.navigateWithoutRouting(`/diagnostics`);
            } else {
              this.router.navigateByUrl(`/${this.classroom.roomNum}/diagnostics`).then();
            }
            return;
          }
        }
        if (this.location.path().includes('/report')) {
          if (appBaseInfo.sessionUser && (appBaseInfo.sessionUser.role === Role.STAFF || appBaseInfo.sessionUser.role === Role.STAFF_ADMIN)) {
            if (this.classroom.roomNum === 0) {
              this.navigationService.navigateWithoutRouting(`/diagnostics/report`);
            } else {
              this.router.navigateByUrl(`/${this.classroom.roomNum}/diagnostics/report`).then();
            }
            return;
          } else {
            if (this.classroom.roomNum === 0) {
              this.navigationService.navigateWithoutRouting(`/diagnostics/login?report=true`);
            } else {
              this.router.navigateByUrl(`/${this.classroom.roomNum}/diagnostics/login?report=true`).then();
            }
            return;
          }
        }

        if (this.location.path().includes('/login?report=true')) {
          return;
        }
        if (this.location.path().includes('/sessionEnded')) {
          return;
        }

        if (!appBaseInfo.sessionUser && !this.location.path().includes('/guest')) {
          // get staff portal auth error id
          const queryParameters = new URLSearchParams(window.location.search);
          const errorId = queryParameters.get('errorId');
          this.router.navigateByUrl(`/${this.classroom.roomNum}/login${errorId ? '?errorId=' + errorId : ''}`).then();
          return;
        }
      }

      if (this.classroom.isIpadMode) {
        this.coreService.sendFullScreenOut().subscribe();
      }

      this.diagnosticsService.updateDiagnosticsState();

      if (this.location.path().includes('/report')) {
        return;
      }
        // Staff user case
      if (this.location.path().includes('/login') && !this.location.path().includes('/loginDetails')) {
        this.router.navigateByUrl(`/${this.classroom.roomNum}/login`).then();
      } else if (appBaseInfo.sessionUser.role !== Role.STUDENT) {
        if (!this.location.path().includes('/loginDetails') /*&& !this.location.path().includes('/login') TODO: ?? commented out.. otherwise user stays on login screen being authorized.. */) {
          if (
            (appBaseInfo.isClassClosed || appBaseInfo.isFirstConnectToRoom) &&
            !this.diagnosticsService.isComingFromDiagnostics()
          ) {
            this.router.navigateByUrl(`/${this.classroom.roomNum}/loginDetails`).then();
            return;
          } else {
            this.classroomInitService.navigateToHome();
            return;
          }
        }
      } else { /* session user is student */
        if (appBaseInfo.isClassClosed) {
          this.router.navigateByUrl(`/${this.classroom.roomNum}/classroomClosed`).then();
          return;
        } else if (appBaseInfo.isInDNDMode && !appBaseInfo.isCurrentClassParticipant) {
          this.router.navigateByUrl(`/${this.classroom.roomNum}/classroomClosed?classId=${appBaseInfo.currentClass.groupId}`).then();
          return;
        } else {
          this.classroomInitService.navigateToHome();
          return;
        }
      }
    })).toPromise()
      .catch(reason => {
        switch (reason.status) {
          case HttpStatusCode.Forbidden: {
            this.router.navigateByUrl(`/${this.classroom.roomNum}/login`).then();
            return;
          }
        }
      });
  }

  public getErrorMessageById(id: number): Observable<{ data: string; message: string }> {
    // @ts-ignore
    return this.http.get(`${this.classroom.vcrURL}/login/error/${id}`);
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });

    this.store.select(RemoteUsersInfoState.getUsersInfo).subscribe(usersInfoById => {
      this.remoteUsers = usersInfoById;
    });

    this.store.select(ClassroomState).subscribe((room: ClassroomStateModel) => {
      this.classroom = room;
    });
  }

  public constructRedirectUrl(classroomNumber: number, userId: number): void {
    // TODO
  }

  public getOnlineUsersIdsByRole(accessType: AccessType): number[] {
    return Object.keys(this.remoteUsers)
      .map(userId => this.remoteUsers[userId])
      .filter((user: RemoteUserAdditionalInfoStateModel) =>
        user.roomConnectionStatus === ConnectionStatus.ONLINE &&
        user.accessType === accessType)
      .map((user: RemoteParticipantInfo) => user.id);
  }

  public createGuest(name: string): any {
    return this.http.post(`${this.classroom.vcrURL}/login/newGuest/${name}`, null);
  }

  //
  // public diagnosticLogin(name: string): any {
  //   return this.http.post(this.classroom.apiURL + '/app/diagnosticLogin/' + name, null);
  // }

  public getGroupLastAssignmentId(studentId, isGroupAssignment): Observable<any> {
    return this.http.get(`${this.classroom.vcrURL}/assignment/get-assignment-url-for-staff?userId=${studentId}&isGroupAssignmentReport=${isGroupAssignment}`);
  }

  private initGroup(): void {
    // Parse '/<num>' or '/<num>/' or '/<num>/<any-string>'
    const pattern = /^\/(0|[1-9]\d*)(?:\/.*|\?.*|$)/;

    const match = window.location.pathname.match(pattern);
    if (match) {
      const roomNum: number = parseInt(match[1]);
      this.store.dispatch(new ClassroomAction.SetClassroom(roomNum));
    }
    this.store.dispatch(new ClassroomAction.SetVCRApiURL(this.contextPath));
  }
}
